import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Award, Target, Lightbulb } from 'lucide-react';
import { mockData } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary to-primary-dark pt-28" style={{ paddingTop: '6.5rem' }}>
        <div className="container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="hero-text mb-6">About NS7VISUALS</h1>
            <p className="text-xl text-gray-800 leading-relaxed">
              We are a passionate team of designers, developers, and strategists dedicated to creating exceptional digital experiences that transform businesses and captivate audiences.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="section-header mb-6">Our Story</h2>
              <div className="space-y-4 text-lg text-gray-700 leading-relaxed">
                <p>
                  Founded in 2023, NS7VISUALS began with a clear vision to make creativity work for brands in real and measurable ways. What started as a small creative team has evolved into a growing studio built on one belief: design should not only look good but also perform.
                </p>
                <p>
                  From our first few projects to full-scale brand systems, we have seen how visuals can connect people, influence audiences, and transform businesses. Every project teaches us something new and shapes how we approach storytelling, strategy, and innovation.
                </p>
                <p>
                  Today, NS7VISUALS stands as a multidisciplinary creative agency that crafts impactful designs, visuals, and strategies to help businesses grow. We stay committed to our mission to create visuals that sell, stories that connect, and brands that scale.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop" 
                alt="Our team at work"
                className="rounded-lg shadow-lg"
              />
              <div className="absolute -bottom-6 -right-6 bg-black text-white p-6 rounded-lg">
                <div className="text-3xl font-bold">10+</div>
                <div className="text-sm">Projects Completed</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Mission & Vision</h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              We're driven by a clear purpose and guided by a compelling vision for the future of design
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="card text-center">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <Target size={24} className="text-white" />
              </div>
              <h3 className="subsection-headers mb-4">Our Mission</h3>
              <p className="text-gray-200 leading-relaxed">
                To empower businesses through exceptional design and creative solutions that drive growth, enhance user experiences, and create lasting brand connections.
              </p>
            </div>
            
            <div className="card text-center">
              <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
                <Lightbulb size={24} className="text-white" />
              </div>
              <h3 className="subsection-headers mb-4">Our Vision</h3>
              <p className="text-gray-200 leading-relaxed">
                To be the leading design agency that sets new standards for creativity, innovation, and client success in the digital design industry.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-padding bg-gray-50">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Our Values</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              These core principles guide everything we do and shape our approach to every project
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {mockData.company.values.map((value, index) => (
              <div key={index} className="card-white text-center group">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                  <value.icon size={20} className="text-white" />
                </div>
                <h3 className="font-semibold text-lg mb-3">{value.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Preview */}
      <section className="section-padding">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="section-header mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Talented individuals who bring passion, expertise, and creativity to every project
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.team.slice(0, 3).map((member, index) => (
              <div key={index} className="card-white text-center group">
                <img 
                  src={member.image} 
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-4 object-cover group-hover:scale-105 transition-transform"
                />
                <h3 className="font-semibold text-lg mb-2">{member.name}</h3>
                <p className="text-gray-600 mb-4">{member.position}</p>
                <p className="text-gray-700 text-sm leading-relaxed">{member.bio.substring(0, 120)}...</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/team" className="btn-primary">
              Meet Full Team <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding bg-white text-black">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="section-header mb-4 text-white">Our Impact</h2>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Numbers that reflect our commitment to excellence and client success
            </p>
          </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-8 text-center">
                      {mockData.stats.map((stat, index) => (
                        <div
                          key={index}
                          className={`space-y-2 ${
                            // ✅ center the last item only on small screens
                            index === mockData.stats.length - 1 ? 'col-span-full mx-auto md:col-span-1' : ''
                          }`}
                        >
                          <div className="text-4xl font-bold text-black">{stat.number}</div>
                          <div className="text-gray-600">{stat.label}</div>
                        </div>
                      ))}
                    </div>
        </div>
      </section>

      {/* CTA Section */}
            <section className="section-padding bg-black text-white">
              <div className="container text-center">
                <h2 className="section-headers mb-4 text-white">Ready to Get Started?</h2>
                <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                  Let's discuss your project and explore how we can help bring your vision to life
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact" className="bg-white text-black px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    Start Your Project
                  </Link>
                  <Link to="/portfolio" className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-black transition-colors">
                    View Our Work
                  </Link>
                </div>
              </div>
            </section>
    </div>
  );
};

export default About;